import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidQueryKey,
  useDeleteApiV1ExamRevisionQuestionQuestionid,
} from '@/shared/api/generated';

/**
 * Exclui uma questão da revisão via
 * `DELETE /api/v1/exam/revision/question/{questionId}`, recarregando a revisão da
 * prova ao concluir. A confirmação do operador (modal) é responsabilidade da UI —
 * este hook só dispara o `DELETE` quando `deleteQuestion` é chamado.
 *
 * @param botExamId - ID da prova bot à qual a questão pertence (para invalidação)
 * @returns Handler de exclusão e estado de loading
 *
 * @example
 * ```tsx
 * const { deleteQuestion, isLoading } = useDeleteRevisionQuestion(botExamId);
 * deleteQuestion(questionId, { onSuccess: () => setDeleteTarget(null) });
 * ```
 */
export function useDeleteRevisionQuestion(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = useDeleteApiV1ExamRevisionQuestionQuestionid({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('question.toast.deleteSuccess') });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionBotexamidQueryKey(botExamId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('question.toast.deleteError') }),
    },
  });

  const deleteQuestion = (questionId: number, callbacks?: { onSuccess?: () => void }) => {
    mutation.mutate(
      { questionId },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { deleteQuestion, isLoading: mutation.isPending } as const;
}
